import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, CheckCircle2, XCircle, RotateCcw, Trophy, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/public/Shared";
import { Button } from "@/components/ui/button";

export default function Kuis() {
  const [questions, setQuestions] = useState([]);
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/quiz").then((r) => setQuestions(r.data)); }, []);

  const q = questions[current];
  const selected = answers[q?.id];

  const choose = (idx) => setAnswers({ ...answers, [q.id]: idx });

  const next = () => {
    if (current < questions.length - 1) setCurrent(current + 1);
    else submit();
  };

  const submit = async () => {
    setSubmitting(true);
    const { data } = await api.post("/quiz/submit", { answers });
    setResult(data);
    setSubmitting(false);
    window.scrollTo(0, 0);
  };

  const reset = () => { setStarted(false); setCurrent(0); setAnswers({}); setResult(null); };

  return (
    <div>
      <PageHero eyebrow="Edukasi Pemilu" title="Kuis Pemilu"
        description="Uji pemahaman Anda tentang pemilu dan pengawasan. Tidak perlu login untuk mengikuti kuis." />
      <div className="container-page py-10 max-w-2xl">
        <Link to="/edukasi" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brandred mb-6">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Edukasi
        </Link>

        {!started && !result && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="quiz-container">
            <div className="h-16 w-16 rounded-2xl bg-brandred/10 text-brandred flex items-center justify-center mx-auto mb-4"><ClipboardList className="h-8 w-8" /></div>
            <h2 className="text-2xl font-extrabold text-navy">Siap Menguji Pengetahuan Anda?</h2>
            <p className="text-slate-500 mt-2">Terdapat {questions.length} pertanyaan pilihan ganda seputar pemilu dan pengawasan.</p>
            <Button onClick={() => setStarted(true)} className="mt-6 bg-brandred hover:bg-brandred-700 text-white rounded-full px-8 h-12 font-semibold" data-testid="start-quiz">Mulai Kuis</Button>
          </div>
        )}

        {started && !result && q && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="quiz-question">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-brandred">Pertanyaan {current + 1} / {questions.length}</span>
              <span className="text-xs text-slate-400">{Object.keys(answers).length} terjawab</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full mb-6 overflow-hidden">
              <div className="h-full bg-brandred transition-all" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
            </div>
            <h3 className="text-lg font-bold text-navy mb-5">{q.question}</h3>
            <div className="space-y-3">
              {q.options.map((opt, idx) => (
                <button key={idx} onClick={() => choose(idx)} data-testid={`quiz-option-${idx}`}
                  className={`w-full text-left px-4 py-3.5 rounded-xl border-2 font-medium transition-all ${selected === idx ? "border-brandred bg-brandred/5 text-navy" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}>
                  <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full mr-3 text-xs font-bold ${selected === idx ? "bg-brandred text-white" : "bg-slate-100 text-slate-500"}`}>{String.fromCharCode(65 + idx)}</span>
                  {opt}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="outline" disabled={current === 0} onClick={() => setCurrent(current - 1)}>Sebelumnya</Button>
              <Button onClick={next} disabled={selected === undefined || submitting} className="bg-navy text-white" data-testid="quiz-next">
                {current === questions.length - 1 ? (submitting ? "Menghitung…" : "Selesai") : "Selanjutnya"}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="quiz-result">
            <div className={`h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-4 ${result.score >= 80 ? "bg-green-100 text-green-600" : result.score >= 50 ? "bg-amber-100 text-amber-600" : "bg-red-100 text-brandred"}`}>
              <Trophy className="h-10 w-10" />
            </div>
            <div className="text-5xl font-extrabold text-navy" data-testid="quiz-score">{result.score}<span className="text-2xl">%</span></div>
            <p className="text-slate-500 mt-1">Nilai Anda</p>
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-surface rounded-xl p-4"><div className="text-2xl font-extrabold text-navy">{result.total}</div><div className="text-xs text-slate-500">Total Soal</div></div>
              <div className="bg-green-50 rounded-xl p-4"><div className="text-2xl font-extrabold text-green-600 flex items-center justify-center gap-1"><CheckCircle2 className="h-5 w-5" />{result.correct}</div><div className="text-xs text-slate-500">Benar</div></div>
              <div className="bg-red-50 rounded-xl p-4"><div className="text-2xl font-extrabold text-brandred flex items-center justify-center gap-1"><XCircle className="h-5 w-5" />{result.wrong}</div><div className="text-xs text-slate-500">Salah</div></div>
            </div>
            <p className="text-slate-700 font-medium mt-6 bg-surface rounded-xl p-4">{result.message}</p>
            <Button onClick={reset} className="mt-6 bg-brandred hover:bg-brandred-700 text-white rounded-full px-7" data-testid="quiz-retry"><RotateCcw className="h-4 w-4 mr-2" /> Ulangi Kuis</Button>
          </div>
        )}
      </div>
    </div>
  );
}
